import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAmazonLink } from "@/hooks/useAmazonLink";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { link: amazonLink } = useAmazonLink();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 z-50 w-full transition-colors duration-500 ${
        scrolled ? "bg-void/80 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4 md:px-12">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <span className="h-2 w-2 shrink-0 bg-rebellion" />
          <span
            data-testid="site-logo"
            className="whitespace-nowrap font-mono text-[0.6rem] uppercase tracking-[0.2em] text-paper sm:text-xs sm:tracking-[0.35em] md:text-sm"
          >
            election on mars
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3 md:gap-4">
          {user ? (
            <button
              data-testid="nav-vault-button"
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 whitespace-nowrap border border-white/20 px-3 py-2 font-mono text-[0.55rem] uppercase tracking-[0.15em] text-paper transition-colors hover:border-rebellion hover:text-rebellion sm:px-4 sm:py-2.5 sm:text-[0.65rem] sm:tracking-[0.2em] md:text-xs"
            >
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="h-5 w-5 object-cover" />
              ) : null}
              The Vault
            </button>
          ) : (
            <button
              data-testid="nav-signin-button"
              onClick={login}
              className="whitespace-nowrap border border-white/20 px-3 py-2 font-mono text-[0.55rem] uppercase tracking-[0.15em] text-paper transition-colors duration-300 hover:border-rebellion hover:text-rebellion sm:px-4 sm:py-2.5 sm:text-[0.65rem] sm:tracking-[0.2em] md:text-xs"
            >
              Sign In
            </button>
          )}

          <a
            data-testid="nav-buy-button"
            href={amazonLink}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden whitespace-nowrap border border-rebellion bg-rebellion px-3 py-2 font-mono text-[0.55rem] font-bold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-transparent hover:text-rebellion sm:px-5 sm:py-2.5 sm:text-[0.65rem] sm:tracking-[0.2em] md:text-xs"
          >
            <span className="sm:hidden">Buy Book</span>
            <span className="hidden sm:inline">Get the Book</span>
          </a>
        </div>
      </div>
    </motion.header>
  );
}
